import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
interface FaqItem {
  id: string;
  question: string;
  answer: React.ReactNode;
}
const faqs: FaqItem[] = [{
  id: "item-1",
  question: "What does a freelance SEO consultant do?",
  answer: "As a freelance SEO consultant, I review your website, carry out keyword research and search intent analysis, identify technical and on-page fixes, improve internal linking, create content briefs, and analyse competitor positions. I also deliver clear progress reporting based on Google Search Console and analytics."
}, {
  id: "item-2",
  question: "How is a freelance SEO consultant different from an SEO agency?",
  answer: <span>
        When you work with an independent SEO consultant, you collaborate directly with the person creating the strategy and executing the work. There are no account managers relaying messages and no layers of junior hand-offs. The right model depends on your business needs, and I do not claim one approach suits everyone. If you are deciding whether to{" "}
        <Link to="/HireSeoConsultantPage" className="text-primary underline font-medium hover:text-primary/80">
          hire an SEO consultant
        </Link>
        , direct access and individual accountability are the primary differences.
      </span>
}, {
  id: "item-3",
  question: "How much does a freelance SEO consultant cost?",
  answer: "Freelance SEO consultant cost depends on the size and complexity of your website, current technical health, how competitive your market is, and the agreed scope of work. Because every site is different, I do not publish fixed packages. I provide a tailored quote and confirm all deliverables and terms in writing before any work begins."
}, {
  id: "item-4",
  question: "Can I hire you for a one-off audit rather than monthly support?",
  answer: <span>
        Yes. You can engage me for a one-off{" "}
        <Link to="/SeoAuditsPage" className="text-primary underline font-medium hover:text-primary/80">
          SEO audit
        </Link>
        , a defined project, or monthly consultancy. A standalone audit provides an independent diagnosis and a prioritised action plan, which works well if you have developers or an in-house team to handle implementation.
      </span>
}, {
  id: "item-5",
  question: "What does monthly freelance SEO consultancy include?",
  answer: "Monthly freelance SEO services typically include ongoing technical checks, content planning and optimisation, monitoring search performance in Google Search Console and analytics, and structured written reporting with agreed next steps. The exact monthly priorities are agreed in writing to suit your commercial goals."
}, {
  id: "item-6",
  question: "Is there a minimum contract length?",
  answer: "Terms depend on the written proposal agreed for your specific project. I outline all engagement terms, notice periods, and deliverables in writing before work begins, rather than imposing arbitrary minimums."
}, {
  id: "item-7",
  question: "How long does SEO take to show results?",
  answer: "The time needed to see organic search improvements varies widely. It depends on your competition, the current condition of your site, how quickly recommended changes can be implemented, and external variables like algorithm updates. Because search involves factors outside any individual's control, I do not promise a specific timeline."
}, {
  id: "item-8",
  question: "Can you guarantee first-page rankings?",
  answer: "No. Search engines control their own search results, so no professional SEO consultant can honestly guarantee a number-one spot or first-page ranking. What I can commit to is careful analysis, clear priorities, considered implementation and honest reporting on progress."
}, {
  id: "item-9",
  question: "Can you work with my in-house team or developers?",
  answer: "Yes. I regularly collaborate with in-house marketers, content writers, and web developers. I supply clear written briefs, plain-English explanations of technical requirements, and shared documents so you and your team can work smoothly within your existing workflow."
}, {
  id: "item-10",
  question: "Is a freelance SEO specialist right for a small business or startup?",
  answer: "Yes. Working with a freelance SEO specialist allows you to agree on a right-sized, tailored scope and focused priorities instead of paying for a rigid standard package. If you are uncertain of the best starting point, a focused audit can help establish clear initial priorities."
}, {
  id: "item-11",
  question: "Do you work with ecommerce businesses on Shopify and WooCommerce?",
  answer: <span>
        Yes. I support online stores on platforms such as Shopify and WooCommerce, assisting with category and product page structure, managing duplicate URLs caused by faceted filters and pagination, and implementing product structured data. You can explore my dedicated{" "}
        <Link to="/EcommerceSeoPage" className="text-primary underline font-medium hover:text-primary/80">
          Ecommerce SEO
        </Link>{" "}
        consultancy for further details.
      </span>
}, {
  id: "item-12",
  question: "Can you help with B2B SEO and lead generation?",
  answer: <span>
        Yes. For B2B organisations, search often involves longer decision cycles and detailed evaluation. I focus on high-intent decision-maker queries, authoritative service pages, and resources structured to generate qualified enquiries. Learn more about my approach to{" "}
        <Link to="/B2BSeoLeadGenerationPage" className="text-primary underline font-medium hover:text-primary/80">
          B2B SEO
        </Link>
        .
      </span>
}, {
  id: "item-13",
  question: "Do you work with clients outside the UK?",
  answer: <span>
        Yes, I collaborate with English-speaking international organisations. All consultancy is delivered remotely, with the UK remaining my core market. You can read more about my{" "}
        <Link to="/InternationalSeoPage" className="text-primary underline font-medium hover:text-primary/80">
          international SEO
        </Link>{" "}
        support.
      </span>
}, {
  id: "item-14",
  question: "How do you report on progress?",
  answer: "I provide structured written reports focusing on organic search visibility, qualified traffic, and agreed commercial goals tracked in Google Search Console and analytics. Every report outlines completed work and recommended next steps, with the reporting cadence agreed up front."
}, {
  id: "item-15",
  question: "How do I get started?",
  answer: <span>
        Send an enquiry through my{" "}
        <Link to="/ContactPage" className="text-primary underline font-medium hover:text-primary/80">
          contact page
        </Link>{" "}
        or email me directly at{" "}
        <a href="mailto:contact@ye-consultancy.com" className="text-primary underline font-medium hover:text-primary/80">
          contact@ye-consultancy.com
        </a>{" "}
        with your website address, market, and main search goals. Email-first contact is welcome, I will review your details, and you and I can determine the most appropriate next step without pressure.
      </span>
}];
export default function Faq() {
  return <section id="faq" aria-labelledby="faq-heading" className="bg-muted/40 py-20 lg:py-28 text-foreground">
      <div className="container mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
          <h2 id="faq-heading" className="text-3xl sm:text-4xl lg:text-5xl font-bold font-serif tracking-tight text-foreground">
            Frequently Asked Questions
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans">
            Clear, practical answers about working with an independent freelance SEO consultant.
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqs.map(faq => <AccordionItem key={faq.id} value={faq.id} className="bg-card border border-border rounded-lg px-5 sm:px-6 transition-colors shadow-2xs">
                <AccordionTrigger className="text-left font-serif text-base sm:text-lg font-semibold text-foreground hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="font-sans text-base text-muted-foreground leading-relaxed pb-5 pt-1">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </div>
    </section>;
}